import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  MEMBER_ROLES,
  useAdminMember,
  useAdminMemberUpdate,
  useBillingAction,
  useChangeSignInEmail,
  useCoverageAction,
  useMemberBilling,
  useMemberCoverage,
  useResendInvitation,
  useSetListservAdded,
  useSetMemberListed,
  type MemberCoverageRow,
} from "@/features/admin/admin";
import { dollars } from "@/features/payments/payments.types";
import type { AdminMemberDetail } from "@/features/members/members.types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DetailSkeleton } from "@/components/Skeletons";
import { sectionTitleClass } from "@/lib/typography";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ListedCell, ListservCell, PaymentCell, TierBadge } from "./MembersAdminPage";

/**
 * Everything an administrator does to one member, on one page: reached by the
 * gear on the member's profile and on their row in the Members table (June,
 * 2026-10-06: "put anything member management related on that page").
 *
 * One card per kind of correction. The table keeps its own directory,
 * listserv and role controls, because those are worked down a column; they
 * are here as well so that nothing about a member needs a second page.
 */
export default function ManageMemberPage() {
  const { id } = useParams();
  const { data: member, isPending, error } = useAdminMember(id);

  if (isPending) return <DetailSkeleton />;
  if (error || !member) {
    return (
      <p className="text-muted-foreground mt-12 text-center">
        {error?.message ?? "This member could not be found."}
      </p>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl">Manage membership</h1>
        <p className="mt-1 text-sm">
          <Link to={`/directory/${member.id}`} className="underline underline-offset-4">
            {member.name || member.email}
          </Link>
        </p>
      </div>
      <MembershipCard member={member} />
      <CoverageCard member={member} />
      <BillingCard member={member} />
      <ListingCard member={member} />
      <SignInAddressCard member={member} />
      {/* Keyed on the saved text, so the form starts again from what the
          server holds after each save. */}
      <ProfileCard
        key={PROFILE_FIELDS.map((k) => member[k] ?? "").join("\u0000")}
        member={member}
      />
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}

type Pending = {
  title: string;
  body: string;
  action: string;
  payload: { tier: string } | { status: string };
  done: string;
};

/** The tier and whether the membership is running. Paying decides both for
 *  most members, so what is offered here is what money cannot say: a gift, and
 *  ending or restoring a membership by hand. Each is confirmed first, with who
 *  gets emailed. */
function MembershipCard({ member }: { member: AdminMemberDetail }) {
  const update = useAdminMemberUpdate();
  const [pending, setPending] = useState<Pending | null>(null);
  const lapsed = member.status === "lapsed";
  // Never over a running paid membership: the card would go on being charged
  // for something the site now called a gift.
  const canGift =
    member.status !== "pending_payment" &&
    (member.tier === "free" || (member.tier === "paid" && lapsed));
  const canRemoveGift = member.tier === "gift" && member.payment.gift_source === "admin";
  const canLapse = member.status === "active" && member.tier !== "preview";

  function run(action: Pending) {
    update.mutate(
      { id: member.id, ...action.payload },
      {
        onSuccess: () => toast.success(action.done),
        onSettled: () => setPending(null),
      }
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Membership</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
          <Fact label="Tier">
            <TierBadge member={member} />
          </Fact>
          <Fact label="Payment">
            <PaymentCell member={member} />
          </Fact>
          <Fact label="Joined">
            {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : "not yet"}
          </Fact>
        </dl>
        <div className="flex flex-wrap gap-2">
          {canGift && (
            <Button
              variant="outline"
              onClick={() =>
                setPending({
                  title: `Give ${member.name} a gift membership?`,
                  body: "They get full access with nothing to pay, and it does not expire. They are emailed to say so.",
                  action: "Give gift",
                  payload: { tier: "gift" },
                  done: "Gift membership given",
                })
              }
            >
              Give gift membership
            </Button>
          )}
          {canRemoveGift && (
            <Button
              variant="outline"
              onClick={() =>
                setPending({
                  title: `Remove ${member.name}'s gift membership?`,
                  body: "They go back to a free membership, unless a sponsor or their organization covers them. They are not emailed.",
                  action: "Remove gift",
                  payload: { tier: "free" },
                  done: "Gift membership removed",
                })
              }
            >
              Remove gift
            </Button>
          )}
          {canLapse && (
            <Button
              variant="outline"
              onClick={() =>
                setPending({
                  title: `Mark ${member.name}'s membership lapsed?`,
                  body: "They see the preview until they renew, and anyone whose membership they pay for loses it too. They are emailed to say so. A subscription in Stripe is not cancelled.",
                  action: "Mark lapsed",
                  payload: { status: "lapsed" },
                  done: "Membership marked lapsed",
                })
              }
            >
              Mark lapsed
            </Button>
          )}
          {lapsed && (
            <Button
              variant="outline"
              disabled={update.isPending}
              onClick={() =>
                run({
                  title: "",
                  body: "",
                  action: "",
                  payload: { status: "active" },
                  done: "Membership reinstated",
                })
              }
            >
              Reinstate
            </Button>
          )}
        </div>
        {update.error && <p className="text-destructive text-sm">{update.error.message}</p>}
      </CardContent>
      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={pending?.title ?? ""}
        description={pending?.body ?? ""}
        confirmLabel={pending?.action}
        busy={update.isPending}
        onConfirm={() => pending && run(pending)}
      />
    </Card>
  );
}

type Ask = { title: string; body: string; action: string; done: string; run: () => void };

/** Membership through an organization: one paid membership there covers its
 *  other stewards. A member can ask for it; here an administrator gives it
 *  without being asked, answers a request, or takes it back. */
function CoverageCard({ member }: { member: AdminMemberDetail }) {
  const { data, isPending, error } = useMemberCoverage(member.id);
  const act = useCoverageAction(member.id);
  const [ask, setAsk] = useState<Ask | null>(null);
  const rows = data?.organizations ?? [];

  const go = (body: Parameters<typeof act.mutate>[0], done: string) =>
    act.mutate(body, {
      onSuccess: () => toast.success(done),
      onSettled: () => setAsk(null),
    });

  function actions(row: MemberCoverageRow) {
    if (row.status === "approved" && row.claim_id !== null) {
      const claim = row.claim_id;
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setAsk({
              title: `Stop covering ${member.name} through ${row.organization}?`,
              body: "They go back to a free membership unless they pay or something else covers them. They are not emailed.",
              action: "Remove coverage",
              done: "Coverage removed",
              run: () => go({ action: "revoke", claim_id: claim }, "Coverage removed"),
            })
          }
        >
          Remove coverage
        </Button>
      );
    }
    if (row.status === "requested" && row.claim_id !== null) {
      const claim = row.claim_id;
      return (
        <>
          <Button
            variant="outline"
            size="sm"
            disabled={act.isPending}
            onClick={() => go({ action: "approved", claim_id: claim }, "Coverage approved")}
          >
            Approve
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={act.isPending}
            onClick={() => go({ action: "declined", claim_id: claim }, "Request declined")}
          >
            Decline
          </Button>
        </>
      );
    }
    if (!row.funded_by_others) return null;
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          setAsk({
            title: `Cover ${member.name} through ${row.organization}?`,
            body: "Their membership is covered for as long as they steward it and somebody there pays. If they pay for themselves, that carries on until it is cancelled. They are emailed to say so.",
            action: "Cover them",
            done: "Membership covered",
            run: () =>
              go({ action: "grant", organization_id: row.organization_id }, "Membership covered"),
          })
        }
      >
        Cover through this organization
      </Button>
    );
  }

  function state(row: MemberCoverageRow) {
    if (row.covering) return "Covering their membership";
    if (row.status === "approved") return "Approved, but nobody there is paying";
    if (row.status === "requested")
      return row.note ? `They asked to be covered: ${row.note}` : "They asked to be covered";
    if (!row.funded_by_others) return "Nobody else there has a paid membership";
    return row.status === "declined"
      ? "Request declined"
      : row.status === "revoked"
        ? "Coverage removed"
        : "Not covering them";
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Organization coverage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {isPending ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-destructive">{error.message}</p>
        ) : rows.length === 0 ? (
          <p>They steward no organization, so there is none to cover them.</p>
        ) : (
          <ul className="divide-y">
            {rows.map((row) => (
              <li
                key={row.organization_id}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/organizations/${row.organization_id}`}
                    className="underline-offset-4 hover:underline"
                  >
                    {row.organization}
                  </Link>
                  <p className="text-muted-foreground">{state(row)}</p>
                </div>
                <div className="flex gap-2">{actions(row)}</div>
              </li>
            ))}
          </ul>
        )}
        {act.error && <p className="text-destructive">{act.error.message}</p>}
      </CardContent>
      <ConfirmDialog
        open={ask !== null}
        onOpenChange={(open) => !open && setAsk(null)}
        title={ask?.title ?? ""}
        description={ask?.body ?? ""}
        confirmLabel={ask?.action}
        busy={act.isPending}
        onConfirm={() => ask?.run()}
      />
    </Card>
  );
}

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : "");

/** What they pay for, read from Stripe, with the three things Soft Network
 *  used to ask Minima for: stop a renewal, end a subscription now, and give a
 *  charge back. Cancelling returns no money and refunding ends nothing, so the
 *  two are separate buttons and each says so. */
function BillingCard({ member }: { member: AdminMemberDetail }) {
  const { data, isPending, error } = useMemberBilling(member.id);
  const act = useBillingAction(member.id);
  const [ask, setAsk] = useState<Ask | null>(null);

  const go = (body: Parameters<typeof act.mutate>[0], done: string) =>
    act.mutate(body, {
      onSuccess: () => toast.success(done),
      onSettled: () => setAsk(null),
    });

  const live = (data?.subscriptions ?? []).filter((s) => s.status !== "canceled");
  const charge = data?.lastCharge ?? null;
  const refundable = charge ? charge.amountCents - charge.refundedCents : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Billing</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {isPending ? (
          <p className="text-muted-foreground">Reading from Stripe…</p>
        ) : error || !data ? (
          <p className="text-destructive">{error?.message ?? "Billing could not be read."}</p>
        ) : !data.hasStripeCustomer ? (
          <p>They have no billing account in Stripe.</p>
        ) : (
          <>
            {live.length === 0 ? (
              <p>No subscription is running.</p>
            ) : (
              <ul className="divide-y">
                {live.map((sub) => {
                  const label = sub.kind === "donation" ? "Donation" : "Membership";
                  const until = day(sub.currentPeriodEnd);
                  return (
                    <li
                      key={sub.id}
                      className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3 first:pt-0 last:pb-0"
                    >
                      <div className="min-w-0 flex-1">
                        <p>
                          {label}
                          {sub.quantity > 1 ? ` × ${sub.quantity}` : ""}, {dollars(sub.amountCents)}
                          {sub.interval ? ` a ${sub.interval}` : ""}
                        </p>
                        <p className="text-muted-foreground">
                          {sub.cancelAtPeriodEnd ? `Ends ${until}` : `Renews ${until}`}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {sub.cancelAtPeriodEnd ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={act.isPending}
                            onClick={() =>
                              go({ action: "resume", subscription_id: sub.id }, "Renewal resumed")
                            }
                          >
                            Resume renewal
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setAsk({
                                title: `Stop this ${label.toLowerCase()} renewing?`,
                                body: `It runs to ${until} and is not charged again. Nothing changes for them until then.`,
                                action: "Stop renewal",
                                done: "Renewal stopped",
                                run: () =>
                                  go(
                                    { action: "period_end", subscription_id: sub.id },
                                    "Renewal stopped"
                                  ),
                              })
                            }
                          >
                            Stop renewal
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setAsk({
                              title: `Cancel this ${label.toLowerCase()} now?`,
                              body:
                                sub.kind === "donation"
                                  ? "It ends at once and is not charged again. No money is returned: refund the charge separately."
                                  : "It ends at once. Their membership ends with it, unless a gift or their organization covers them, and they are emailed if it does. No money is returned: refund the charge separately.",
                              action: "Cancel now",
                              done: "Subscription cancelled",
                              run: () =>
                                go({ action: "now", subscription_id: sub.id }, "Subscription cancelled"),
                            })
                          }
                        >
                          Cancel now
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
            {charge && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t pt-4">
                <p className="min-w-0 flex-1">
                  Last charge: {dollars(charge.amountCents)} on {day(charge.created)}
                  {charge.refundedCents > 0 && (
                    <span className="text-muted-foreground">
                      {" "}
                      ({dollars(charge.refundedCents)} refunded)
                    </span>
                  )}
                </p>
                {refundable > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setAsk({
                        title: `Refund ${dollars(refundable)} to ${member.name}?`,
                        body: "It goes back to the card it came from and cannot be undone. Their membership and any subscription are not changed.",
                        action: `Refund ${dollars(refundable)}`,
                        done: "Charge refunded",
                        run: () => go({ action: "refund", charge_id: charge.id }, "Charge refunded"),
                      })
                    }
                  >
                    Refund {dollars(refundable)}
                  </Button>
                )}
              </div>
            )}
            {data.payments.length > 0 && (
              <ul className="text-muted-foreground space-y-1 border-t pt-4">
                {data.payments.map((p) => (
                  <li key={p.id} className="flex justify-between gap-4">
                    <span>
                      {day(p.created_at)} {p.kind}
                    </span>
                    <span>{dollars(p.amount_cents)}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {act.error && <p className="text-destructive">{act.error.message}</p>}
      </CardContent>
      <ConfirmDialog
        open={ask !== null}
        onOpenChange={(open) => !open && setAsk(null)}
        title={ask?.title ?? ""}
        description={ask?.body ?? ""}
        confirmLabel={ask?.action}
        destructive
        busy={act.isPending}
        onConfirm={() => ask?.run()}
      />
    </Card>
  );
}

/** The three controls the Members table also carries, for one member. */
function ListingCard({ member }: { member: AdminMemberDetail }) {
  const listing = useSetMemberListed();
  const listserv = useSetListservAdded();
  const update = useAdminMemberUpdate();
  const failure = listing.error || listserv.error || update.error;
  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Directory, listserv and role</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
          <Fact label="Directory">
            <ListedCell
              member={member}
              onToggle={(listed, onError) =>
                listing.mutate({ id: member.id, listed }, { onError })
              }
            />
          </Fact>
          <Fact label="Listserv">
            <ListservCell
              member={member}
              busy={listserv.isPending}
              onSet={(state) => listserv.mutate({ id: member.id, state })}
            />
          </Fact>
          <Fact label="Role">
            <Select
              value={member.role}
              disabled={update.isPending}
              onValueChange={(role) => update.mutate({ id: member.id, role })}
            >
              <SelectTrigger aria-label={`Role for ${member.name}`} size="sm" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MEMBER_ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Fact>
        </dl>
        {failure && <p className="text-destructive text-sm">{failure.message}</p>}
      </CardContent>
    </Card>
  );
}

/** Moving a member who has never signed in to another address. Once they have
 *  signed in the address is theirs, and the card says so in place of the
 *  field. */
function SignInAddressCard({ member }: { member: AdminMemberDetail }) {
  const change = useChangeSignInEmail();
  const resend = useResendInvitation();
  const [email, setEmail] = useState("");
  const [invite, setInvite] = useState(true);
  const address = email.trim();

  function save(event: React.FormEvent) {
    event.preventDefault();
    change.mutate(
      { id: member.id, email: address, invite },
      {
        onSuccess: (result) => {
          setEmail("");
          toast.success(
            result.invited
              ? `Address changed and invitation sent to ${result.email}`
              : `Address changed to ${result.email}`
          );
        },
      }
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Sign-in address</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {member.signed_in ? (
          <p>
            They sign in with <strong className="font-medium">{member.email}</strong> and
            change it themselves on their Account page.
          </p>
        ) : (
          <>
            <p>
              They have not signed in yet. Their membership is waiting under{" "}
              <strong className="font-medium">{member.email}</strong>, and whoever signs in
              with the new address receives it.
            </p>
            <form onSubmit={save} className="space-y-4">
              <div className="max-w-sm space-y-2">
                <Label htmlFor="sign-in-address">New address</Label>
                <Input
                  id="sign-in-address"
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-3">
                <Switch id="sign-in-invite" checked={invite} onCheckedChange={setInvite} />
                <Label htmlFor="sign-in-invite">Email them the invitation</Label>
              </div>
              {(change.error || resend.error) && (
                <p className="text-destructive">
                  {(change.error || resend.error)?.message}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button type="submit" disabled={!address.includes("@") || change.isPending}>
                  {change.isPending && <Spinner data-slot="icon" />}
                  Change address
                </Button>
                {/* The address is right and the mail was missed: the other
                    half of Sami's question. Same invitation, nothing changed. */}
                <Button
                  type="button"
                  variant="outline"
                  disabled={change.isPending || resend.isPending}
                  onClick={() =>
                    resend.mutate(
                      { id: member.id },
                      {
                        onSuccess: (r) => toast.success(`Invitation sent to ${r.email}`),
                      }
                    )
                  }
                >
                  {resend.isPending && <Spinner data-slot="icon" />}
                  Resend invitation
                </Button>
              </div>
            </form>
          </>
        )}
      </CardContent>
    </Card>
  );
}

const PROFILE_FIELDS = ["name", "title", "location", "bio", "ip"] as const;
type ProfileField = (typeof PROFILE_FIELDS)[number];
const PROFILE_LABELS: Record<ProfileField, string> = {
  name: "Name",
  title: "Title",
  location: "Location",
  bio: "Bio",
  ip: "Intellectual property",
};

/** An administrator's edit of somebody else's profile text (Sami, 2026-09-29).
 *  Text only: the published addresses stay the member's to verify, the photo
 *  stays theirs, and organizations have their own page. Sends only the fields
 *  that changed, so a save never rewrites what it did not touch. */
function ProfileCard({ member }: { member: AdminMemberDetail }) {
  const queryClient = useQueryClient();
  const update = useAdminMemberUpdate();
  const initial = Object.fromEntries(
    PROFILE_FIELDS.map((k) => [k, (member[k] as string | undefined) ?? ""])
  ) as Record<ProfileField, string>;
  const [form, setForm] = useState(initial);
  const changed = PROFILE_FIELDS.filter((k) => form[k] !== initial[k]);

  function save(event: React.FormEvent) {
    event.preventDefault();
    const payload = Object.fromEntries(changed.map((k) => [k, form[k]]));
    update.mutate(
      { id: member.id, ...payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["afell", "member"] });
          queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
          toast.success("Profile saved");
        },
      }
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Profile</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-3">
          {PROFILE_FIELDS.map((key) => (
            <div key={key} className="space-y-2">
              <Label htmlFor={`admin-profile-${key}`}>{PROFILE_LABELS[key]}</Label>
              {key === "bio" || key === "ip" ? (
                <Textarea
                  id={`admin-profile-${key}`}
                  rows={key === "bio" ? 6 : 3}
                  className="max-h-64"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              ) : (
                <Input
                  id={`admin-profile-${key}`}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              )}
            </div>
          ))}
          {update.error && <p className="text-destructive text-sm">{update.error.message}</p>}
          <Button
            type="submit"
            disabled={!changed.length || !form.name.trim() || update.isPending}
          >
            {update.isPending && <Spinner data-slot="icon" />}
            Save profile
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
